import { Injectable } from '@angular/core';
import {Observable} from 'rxjs';
import {
  EmailLogModel, UserConnectionPostRequest, UserConnectionPostRequestBatch, UserConnectionResponse,
  UserListRequest,
  UserModel,
  UserRejectionModel
} from '../../../types/api/dot-net-api-types/data-contracts';
import { HttpClient, HttpParams } from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {userEndpoints} from '../../../types/api/endpoints/user.endpoints';

/**
 * This is a stub for the UsersApiService.
 */
export interface IUsersApiService {
  activateUsers(userIds: string[]): Observable<object>;
  approveUsers(userIds: string[]): Observable<object>;
  createUser(user: UserModel): Observable<UserModel>;
  deactivateUsers(userIds: string[]): Observable<object>;
  deleteUsers(userIds: string[]): Observable<object>;
  getEmailHistory(userId: string): Observable<EmailLogModel[]>;
  getEmail(emailLogId: string): Observable<string>;
  getNewUsers(searchBy?: string): Observable<UserModel[]>;
  getMatchingUsers(userId: string): Observable<UserModel[]>;
  getUserInfo(userId: string): Observable<UserModel>;
  rejectUsers(userRejections: UserRejectionModel[]): Observable<object>;
  resendWelcomeEmail(userId: string): Observable<object>;
  updateUser(user: UserModel): Observable<UserModel>;
  userList(userListRequest: UserListRequest): Observable<UserModel[]>;
  userAcceptTerms(): Observable<object>;
  postConnection(fromUserId: string, toUserId: string): Observable<UserConnectionResponse[]>;
  batchPostConnection(fromUserId: string, toUserIds: string[]): Observable<UserConnectionResponse[]>;
  deleteConnection(userId: string): Observable<UserConnectionResponse[]>;
  getConnections(userId: string): Observable<UserConnectionResponse[]>;
}
/**
 * Base url for the api.
 */
const BASE_URL = environment.ApiServer;
/**
 * Users Endpoints for the api.
 */
const USERS_ENDPOINTS = userEndpoints(BASE_URL);

@Injectable({
  providedIn: 'root'
})
export class UsersApiService implements IUsersApiService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Endpoint to activate a user.
   * @param userIds
   */
  activateUsers(userIds: string[]): Observable<object> {
    const url = USERS_ENDPOINTS.activateUser();
    return this.http.put(url, userIds) as Observable<object>;
  }

  /**
   * Endpoint to approve a user.
   * @param userIds
   */
  approveUsers(userIds: string[]): Observable<object> {
    const url = USERS_ENDPOINTS.approveUser();
    return this.http.put(url, userIds) as Observable<object>;
  }

  createUser(user: UserModel): Observable<UserModel> {
    const url = USERS_ENDPOINTS.createUser();
    return this.http.post(url, user) as Observable<UserModel>;
  }

  /**
   * Endpoint to deactivate a user.
   * @param userIds
   */
  deactivateUsers(userIds: string[]): Observable<object> {
    const url = USERS_ENDPOINTS.deActivateUser();
    return this.http.put(url, userIds) as Observable<object>;
  }

  /**
   * Endpoint to delete a user.
   * @param userIds
   */
  deleteUsers(userIds: string[]): Observable<object> {
    const url = USERS_ENDPOINTS.deleteUser();
    return this.http.delete(url, {
      body: userIds
    }) as Observable<object>;

  }

  /**
   * Endpoint to get email history.
   * @param userId
   */
  getEmailHistory(userId: string): Observable<EmailLogModel[]> {
    const url = USERS_ENDPOINTS.emailLog(userId);
    return this.http.get(url) as Observable<EmailLogModel[]>;
  }

  /**
   * Endpoint to get single HTML email
   * 
   * @param emailLogId The email id of the HTML email to retrieve.
   * 
   * @returns A single HTML email as a string.
   */
  getEmail(emailLogId: string): Observable<string> {
    const url = USERS_ENDPOINTS.email(emailLogId);
    return this.http.get(url, { responseType: 'text' }) as Observable<string>;
  }

  /**
   * Endpoint to get new users.
   * @param searchBy
   */
  getNewUsers(searchBy?: string): Observable<UserModel[]> {
    const url = USERS_ENDPOINTS.newUsers();
    // Still viable?
    const params: HttpParams = new HttpParams();
    if (searchBy) {
      params.set('searchBy', searchBy);
    }
    return this.http.get(url) as Observable<UserModel[]>;
  }

  /**
   * Endpoint to get matching users.
   * @param userId
   */
  getMatchingUsers(userId: string): Observable<UserModel[]> {
    const url = USERS_ENDPOINTS.matchingUsers(userId);
    return this.http.get(url) as Observable<UserModel[]>;
  }

  /**
   * Endpoint to get user info.
   * @param userId
   */
  getUserInfo(userId: string): Observable<UserModel> {
    const url = USERS_ENDPOINTS.userInfo(userId);
    return this.http.get(url) as Observable<UserModel>;
  }

  /**
   * Endpoint to reject a user.
   * @param userRejections
   */
  rejectUsers(userRejections: UserRejectionModel[]): Observable<object> {
    const url = USERS_ENDPOINTS.rejectUser();
    return this.http.put(url, userRejections) as Observable<object>;
  }

  /**
   * Endpoint to resend a welcome email.
   * @param userId
   */
  resendWelcomeEmail(userId: string): Observable<object> {
    const url = USERS_ENDPOINTS.resendWelcomeEmail(userId);
    return this.http.post(url, {}) as Observable<object>;
  }

  /**
   * Endpoint to update a user.
   * @param user
   */
  updateUser(user: UserModel): Observable<UserModel> {
    const url = USERS_ENDPOINTS.updateUser();
    return this.http.put(url, user) as Observable<UserModel>;
  }

  /**
   * Endpoint to get a list of users.
   * @param userListRequest
   */
  userList(userListRequest: UserListRequest): Observable<UserModel[]> {
    const url = USERS_ENDPOINTS.userList();
    return this.http.post(url, userListRequest) as Observable<UserModel[]>;
  }

  /**
   * Endpoint to accept terms.
   */
  userAcceptTerms(): Observable<object> {
    const url = USERS_ENDPOINTS.userAcceptTerms();
    return this.http.put(url, {}) as Observable<object>;
  }

  /**
   * Endpoint to post connections.
   * @param fromUserId The user id of the user making the connection, (I.E. a judge)
   * @param toUserId The user id of the user being connected to, (I.E. an assistant councel)
   */
  postConnection(fromUserId: string, toUserId: string): Observable<UserConnectionResponse[]> {
    const url = USERS_ENDPOINTS.connections();
    const data: UserConnectionPostRequest = {
      fromUserId: fromUserId,
      toUserId: toUserId
    }
    return this.http.post(url, data) as Observable<UserConnectionResponse[]>;
  }

  /**
   * Endpoint to batch post connections.
   * @param fromUserId
   * @param toUserIds
   */
  batchPostConnection(fromUserId: string, toUserIds: string[]): Observable<UserConnectionResponse[]> {
    const url = USERS_ENDPOINTS.batchConnections();
    const data: UserConnectionPostRequestBatch = {
      fromUserId: fromUserId,
      toUserIds: toUserIds
    };
    return this.http.post(url, data) as Observable<UserConnectionResponse[]>;
  }

  /**
   * Endpoint to delete a connection.
   * @param userId The id of the user that has a connection (i.e. an assistant councel)
   */
  deleteConnection(userId: string): Observable<UserConnectionResponse[]> {
    const url = USERS_ENDPOINTS.connections(userId);
    return this.http.delete(url, {}) as Observable<UserConnectionResponse[]>;
  }

  /**
   * Endpoint to get a connection.
   * @param userId The id of the user that has a connection (i.e. judge)
   */
  getConnections(userId:string): Observable<UserConnectionResponse[]> {
    const url = USERS_ENDPOINTS.connections(userId);
    return this.http.get(url) as Observable<UserConnectionResponse[]>;
  }
}
